import type { AssessmentPoints } from '@app/src/components/Configurations/SustainabilityLibraryPages/Assessments/utils/types'
import type { QuestionAnswerClassificationRule } from '@app/src/types/resourceExplorer'
import { AnswerRuleType, QuestionAnswerRank } from '@app/src/types/resourceExplorer'
import type { Palette } from '@mui/material'
import { Box, Chip, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface ClassificationRowProps {
  item?: QuestionAnswerClassificationRule
  translationKey: string
  showChip?: boolean
  points?: Omit<AssessmentPoints, 'isCustom'>
}

export const getSxByRank = (palette: Palette, rank?: QuestionAnswerRank) => {
  switch (rank) {
    case QuestionAnswerRank.Positive:
      return {
        color: 'common.white',
        bgcolor: palette.brandDecorative.emerald,
      }
    case QuestionAnswerRank.VeryPositive:
      return {
        color: 'common.white',
        bgcolor: palette.brand.monsteraGreen,
      }
    case QuestionAnswerRank.Negative:
      return {
        color: 'primary',
        bgcolor: palette.error.main,
      }
    case QuestionAnswerRank.NegativeAdverse:
      return {
        color: 'common.white',
        bgcolor: palette.semantic.error,
      }
    case QuestionAnswerRank.Neutral:
      return {
        color: 'primary',
        bgcolor: palette?.grey[200],
      }
    default:
      return {}
  }
}

export const groupClassificationsByRuleType = (
  questionAnswerClassifications: QuestionAnswerClassificationRule[] | undefined = [],
) => {
  const definedQuestionAnswerClassifications = questionAnswerClassifications.filter(
    item => item?.rank !== QuestionAnswerRank.NotSet,
  )

  const optionClassifications = definedQuestionAnswerClassifications.filter(
    answerClassification => answerClassification?.ruleType === AnswerRuleType.SelectedValue,
  )

  const numberClassifications = definedQuestionAnswerClassifications.filter(
    answerClassification =>
      answerClassification?.ruleType === AnswerRuleType.NumberEqualTo ||
      answerClassification?.ruleType === AnswerRuleType.NumberGreaterThan ||
      answerClassification?.ruleType === AnswerRuleType.NumberLowerThan,
  )

  const answerClassifications = definedQuestionAnswerClassifications.filter(
    answerClassification => answerClassification?.ruleType === AnswerRuleType.AnswerStatus,
  )

  return { optionClassifications, numberClassifications, answerClassifications }
}

const ClassificationRow: React.FC<ClassificationRowProps> = ({ item, translationKey, showChip = true, points }) => {
  const { formatMessage } = useIntl()

  const getPointsValue = () => {
    if (!points || !item?.rank) {
      return 0
    }

    switch (item.rank) {
      case QuestionAnswerRank.VeryPositive:
        return points.veryPositive
      case QuestionAnswerRank.Positive:
        return points.positive
      case QuestionAnswerRank.Negative:
        return points.negative
      case QuestionAnswerRank.NegativeAdverse:
        return points.negativeAdverse
      case QuestionAnswerRank.Neutral:
        return points.neutral
      default:
        return 0
    }
  }

  const pointsValue = getPointsValue()

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      key={item?.rank}
      borderTop="1px solid"
      borderColor="divider"
      py={1.5}
      px={2}
    >
      <Typography sx={{ flex: 3, mr: 2 }}>
        {formatMessage({ id: translationKey }, { ruleType: item?.ruleType?.toString(), value: item?.value })}
      </Typography>
      <Box display="flex" justifyContent="left" minWidth="auto">
        {showChip && (
          <Chip
            label={formatMessage({
              id: `templateBuilder.answerClassifications.classificationsRanks.${item?.rank}`,
            })}
            size="small"
            sx={({ palette }) => ({ ...getSxByRank(palette, item?.rank) })}
          />
        )}
      </Box>
      {Boolean(points) && (
        <Typography sx={{ textAlign: 'right', minWidth: '120px' }}>
          {formatMessage(
            { id: 'baselineAssessment.providerPage.points' },
            {
              points: pointsValue,
            },
          )}
        </Typography>
      )}
    </Stack>
  )
}

export default ClassificationRow
